import { startLogin } from '../utils/session';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Sun, Moon, Monitor } from 'lucide-react';
import api from '../services/api';
import { useEntitlements } from '../store/EntitlementStore';
import { useTheme } from '../store/ThemeContext';
import { PasswordResetModal } from '../components/PasswordResetModal';
import { MFAEnrollmentModal } from '../components/MFAEnrollmentModal';
import { MFAVerificationModal } from '../components/MFAVerificationModal';
import './LoginPage.css';

const defaultFaviconSrc = `${process.env.PUBLIC_URL || ''}/favicon.svg`;

const ErrorAlertIcon = () => (
  <svg className="login-alert__icon" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
      clipRule="evenodd"
    />
  </svg>
);

const TrustCheckIcon = () => (
  <svg className="login-page__trust-check" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

/**
 * Login page - Email + password authentication with tenant branding support.
 */
export const LoginPage = () => {
  const navigate = useNavigate();
  const { fetchCurrentUser } = useEntitlements();
  const { theme, toggleTheme } = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [, setMfaEnrollmentRequired] = useState(false);
  const [, setMfaVerificationRequired] = useState(false);
  const [showMFAEnrollment, setShowMFAEnrollment] = useState(false);
  const [showMFAVerification, setShowMFAVerification] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  // Tenant branding state
  const [branding, setBranding] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(true);
  const [brandingError, setBrandingError] = useState(null);

  const getThemeIcon = () => {
    if (theme === 'light') return Sun;
    if (theme === 'dark') return Moon;
    return Monitor;
  };
  const ThemeIcon = getThemeIcon();

  const themeToggleLabel =
    theme === 'light'
      ? 'Switch theme (currently light)'
      : theme === 'dark'
        ? 'Switch theme (currently dark)'
        : 'Switch theme (currently matching system)';

  const formatRetryWait = (retryAfterSeconds) => {
    const totalSeconds = Number.isFinite(retryAfterSeconds)
      ? Math.max(0, Math.ceil(retryAfterSeconds))
      : 0;
    if (totalSeconds <= 0) {
      return 'a moment';
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      if (minutes > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${hours}h`;
    }
    if (minutes > 0) {
      return `${minutes}m`;
    }
    return `${seconds}s`;
  };

  const getLoginErrorMessage = (err) => {
    const statusCode = err.response?.status;
    const errorData = err.response?.data || {};
    const isThrottled =
      statusCode === 429 ||
      errorData.code === 'throttle_exceeded' ||
      errorData.code === 'throttled';

    if (isThrottled) {
      const waitFor = formatRetryWait(
        Number(errorData.retry_after_seconds ?? errorData.wait ?? 0)
      );
      return `Too many sign-in attempts. Please wait ${waitFor} before trying again.`;
    }

    return (
      errorData.detail ||
      errorData.error ||
      'Invalid email or password'
    );
  };

  // Fetch tenant branding on mount
  useEffect(() => {
    const fetchBranding = async () => {
      // Extract subdomain from hostname
      const hostname = window.location.hostname;
      const mainDomain = process.env.REACT_APP_MAIN_DOMAIN || 'portalrapp.com';

      // Check if subdomain exists
      let tenantSlug = null;

      // Handle localhost/127.0.0.1 (development)
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        setBrandingLoading(false);
        return;
      }

      // Check if this is the main domain (exact match or www subdomain)
      if (hostname === mainDomain || hostname === `www.${mainDomain}`) {
        // Main domain - no tenant branding
        setBrandingLoading(false);
        return;
      }

      // Check if hostname ends with main domain (indicating a subdomain)
      // e.g., tenant.portalrapp.com ends with .portalrapp.com
      if (hostname.endsWith(`.${mainDomain}`)) {
        // Extract subdomain (everything before .portalrapp.com)
        const subdomain = hostname.replace(`.${mainDomain}`, '');

        // Reserved subdomains don't have tenant branding
        if (subdomain && !['www', 'api', 'app', 'admin'].includes(subdomain)) {
          tenantSlug = subdomain;
        }
      }

      // If no tenant slug, use generic branding
      if (!tenantSlug) {
        setBrandingLoading(false);
        return;
      }

      try {
        const response = await api.get(`/v1/public/tenant-branding/${tenantSlug}/`);
        const data = response.data;
        setBranding(data);
        setBrandingLoading(false);

        // Apply custom colors to CSS variables
        if (data.primary_color) {
          document.documentElement.style.setProperty('--primary-color', data.primary_color);
        }
        if (data.secondary_color) {
          document.documentElement.style.setProperty('--secondary-color', data.secondary_color);
        }
        if (data.accent_color) {
          document.documentElement.style.setProperty('--accent-color', data.accent_color);
        }

        // Update favicon if provided
        if (data.favicon_url) {
          // Remove existing favicon links
          const existingLinks = document.querySelectorAll('link[rel*="icon"]');
          existingLinks.forEach((link) => link.remove());

          // Determine MIME type based on file extension
          let mimeType = 'image/png';
          if (data.favicon_url.endsWith('.ico')) {
            mimeType = 'image/x-icon';
          } else if (data.favicon_url.endsWith('.svg')) {
            mimeType = 'image/svg+xml';
          }

          // Create new favicon link
          const link = document.createElement('link');
          link.rel = 'icon';
          link.type = mimeType;
          link.href = data.favicon_url;
          document.head.appendChild(link);
        }

        // Update title and description if company name is provided
        if (data.company_name) {
          document.title = `${data.company_name} Portal`;

          let metaDescription = document.querySelector('meta[name="description"]');
          if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.setAttribute('name', 'description');
            document.head.appendChild(metaDescription);
          }
          metaDescription.setAttribute('content', `${data.company_name} Portal`);
        }
      } catch (err) {
        console.error('Error fetching branding:', err);
        if (err.response?.status === 404) {
          setBrandingError('Invalid subdomain. This tenant does not exist.');
        } else {
          setBrandingError('Failed to load tenant branding.');
        }
        setBrandingLoading(false);
      }
    };

    fetchBranding();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Don't allow login if there's a branding error (invalid subdomain)
    if (brandingError) {
      setError('Cannot login with an invalid subdomain.');
      return;
    }

    setError(null);
    setLoading(true);

    // Keycloak authenticates now (MP-MIG-001). The BFF starts the
    // authorization-code flow, holds the tokens server-side, and the browser
    // gets one HttpOnly cookie — so there is nothing here to post, store or
    // refresh, and no password passes through this page at all.
    //
    // `next` is a path, never an absolute URL: `_safe_next` on the server
    // refuses anything else, and an open redirect on a login page is how a
    // convincing phishing link gets built.
    const next = new URLSearchParams(window.location.search).get('next');
    startLogin(next && next.startsWith('/') ? next : '/');
  };

  const faviconSrc = branding?.favicon_url || defaultFaviconSrc;
  const isTenantBranded = Boolean(branding);

  const renderToolbar = () => (
    <div className="login-page__toolbar">
      <button
        type="button"
        className="login-page__theme-toggle"
        onClick={() => toggleTheme()}
        aria-label={themeToggleLabel}
      >
        <ThemeIcon className="login-page__theme-toggle-icon" strokeWidth={2} aria-hidden="true" />
      </button>
    </div>
  );

  // Show loading while fetching branding
  if (brandingLoading) {
    return (
      <div className="login-page login-page--generic">
        {renderToolbar()}
        <div className="login-page__main">
          <div className="login-page__column login-page__column--loading">
            <div className="login-page__loading-brand" aria-busy="true" aria-live="polite">
              <div className="login-page__mark login-page__mark--loading">
                <img src={defaultFaviconSrc} alt="" className="login-page__mark-img" />
              </div>
              <Loader2 className="login-page__loading-spinner" aria-hidden="true" strokeWidth={2} />
              <span className="login-page__loading-text">Loading…</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`login-page ${isTenantBranded ? 'login-page--tenant' : 'login-page--generic'}`}
      style={{
        backgroundImage: branding?.login_background_url
          ? `url(${branding.login_background_url})`
          : undefined,
      }}
    >
      {isTenantBranded && branding?.login_background_url ? (
        <div className="login-page__hero-scrim" aria-hidden="true" />
      ) : null}

      {renderToolbar()}

      <div className="login-page__main">
        <div className="login-page__column">
          <header className="login-page__header">
            <div className="login-page__brand">
              {isTenantBranded ? (
                branding.logo_url ? (
                  <div className="login-logo">
                    <img src={branding.logo_url} alt={branding.company_name || ''} />
                  </div>
                ) : (
                  <div className="login-page__favicon-wrap">
                    <img src={faviconSrc} alt="" className="login-page__favicon" />
                  </div>
                )
              ) : (
                <div className="login-page__mark" aria-hidden="true">
                  <span className="login-page__mark-text">P</span>
                </div>
              )}
            </div>

            <div className="login-header">
              <h1>
                {branding?.company_name ? `${branding.company_name} Portal` : 'Portalr'}
              </h1>
              <p>
                {isTenantBranded
                  ? branding?.welcome_message || 'Sign in to your account'
                  : 'Welcome back! Please sign in to your account.'}
              </p>
            </div>
          </header>

          <div className="login-card">
            {brandingError ? (
              <div className="login-alert login-alert--danger" role="alert">
                <ErrorAlertIcon />
                <p className="login-alert__text">
                  <strong>Error:</strong> {brandingError}
                </p>
              </div>
            ) : null}

            {!brandingError && error ? (
              <div className="login-alert login-alert--danger" role="alert">
                <ErrorAlertIcon />
                <p className="login-alert__text">{error}</p>
              </div>
            ) : null}

            {!brandingError ? (
              <form onSubmit={handleSubmit} className="login-form">
                {/* No credential fields (MP-MIG-001). Keycloak collects them,
                    and a form here that threw away what was typed into it just
                    made people enter their password twice. Said out loud rather
                    than left as a bare button, because an unexplained redirect
                    to another domain is what a phishing page looks like. */}
                <p className="login-form__sso-note">
                  You&rsquo;ll sign in with your Mamba Platform account.
                </p>

                <button type="submit" className="btn-login" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="btn-login__spinner" aria-hidden="true" strokeWidth={2} />
                      Redirecting&hellip;
                    </>
                  ) : (
                    'Continue to sign in'
                  )}
                </button>
              </form>
            ) : null}

            {!brandingError ? (
              <>
                <div className="login-card__divider">
                  <span className="login-card__divider-label">
                    {isTenantBranded ? 'New here?' : 'New to Portalr?'}
                  </span>
                </div>
                <a href="/request-access" className="login-btn-secondary">
                  Request Access
                </a>
              </>
            ) : null}
          </div>

          <div className="login-page__below-card">
            {branding?.support_email ? (
              <p className="login-support">
                Need help? Contact{' '}
                <a href={`mailto:${branding.support_email}`}>{branding.support_email}</a>
              </p>
            ) : null}
            <p className="login-page__privacy">
              <Link to="/privacy">Privacy Policy</Link>
            </p>
          </div>

          {!isTenantBranded ? (
            <div className="login-page__trust">
              <p className="login-page__trust-lead">
                Secure, seamless client experiences—without the chaos
              </p>
              <div className="login-page__trust-badges" role="list">
                <div className="login-page__trust-item" role="listitem">
                  <TrustCheckIcon />
                  <span>Secure</span>
                </div>
                <div className="login-page__trust-item" role="listitem">
                  <TrustCheckIcon />
                  <span>Reliable</span>
                </div>
                <div className="login-page__trust-item" role="listitem">
                  <TrustCheckIcon />
                  <span>Fast</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {showPasswordReset && (
        <PasswordResetModal
          onSuccess={async () => {
            // After successful password reset, fetch user and navigate
            await fetchCurrentUser();
            setShowPasswordReset(false);
            navigate('/');
          }}
          onCancel={() => {
            // Logout user if they cancel password reset
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setShowPasswordReset(false);
          }}
        />
      )}

      {showMFAEnrollment && (
        <MFAEnrollmentModal
          isOpen={showMFAEnrollment}
          onClose={() => {
            setShowMFAEnrollment(false);
            setMfaEnrollmentRequired(false);
            setPendingUser(null);
          }}
          onSuccess={async () => {
            // After enrollment, proceed to verification
            setShowMFAEnrollment(false);
            setMfaVerificationRequired(true);
            setShowMFAVerification(true);
          }}
        />
      )}

      {showMFAVerification && (
        <MFAVerificationModal
          isOpen={showMFAVerification}
          onClose={() => {
            setShowMFAVerification(false);
            setMfaVerificationRequired(false);
            setPendingUser(null);
          }}
          onSuccess={async (data) => {
            // Store tokens from MFA verification response
            if (data.access_token) {
              localStorage.setItem('access_token', data.access_token);
              if (data.refresh_token) {
                localStorage.setItem('refresh_token', data.refresh_token);
              }

              // Fetch user data
              await fetchCurrentUser();

              // Close modal and navigate
              setShowMFAVerification(false);
              setMfaVerificationRequired(false);
              setPendingUser(null);
              navigate('/');
            }
          }}
          userEmail={pendingUser?.email || formData.email}
        />
      )}
    </div>
  );
};
